<template>
  <div class="inscricao-public">
    <header class="inscricao-header">
      <h1 class="logo">Acamp <span>El Shaday</span></h1>
      <p class="subtitle">Ficha de inscrição</p>
    </header>

    <main class="inscricao-content">
      <!-- Loading -->
      <div v-if="state === 'loading'" class="state-card">
        <div class="spinner"></div>
        <p>Carregando suas informações...</p>
      </div>

      <!-- Erro de busca / 404 -->
      <div v-else-if="state === 'notFound'" class="state-card state-error">
        <i class="fa fa-exclamation-triangle"></i>
        <h2>Inscrição não encontrada</h2>
        <p>O link que você abriu não corresponde a nenhuma inscrição. Confira o link enviado pela coordenação.</p>
      </div>

      <!-- Já preenchida + QR -->
      <div v-else-if="state === 'alreadySubmitted'" class="state-card state-success">
        <div class="check-icon">
          <i class="fa fa-check-circle"></i>
        </div>
        <h2>Ficha enviada!</h2>
        <p>Recebemos sua ficha de inscrição, {{ acampante.name?.split(' ')[0] }}.</p>
        <p v-if="fichaPreenchidaEm" class="ficha-date">Enviada em {{ formatDate(fichaPreenchidaEm) }}</p>

        <!-- QR Code do checkin -->
        <div class="qr-section" id="qr-section">
          <h3 class="qr-title">📲 Seu QR Code de check-in</h3>
          <p class="qr-instructions">
            Salve esse QR Code no seu celular. <strong>Ele é necessário pra entrar no acamp.</strong>
            Apresente ao voluntário no portão de entrada.
          </p>
          <div class="qr-frame" ref="qrFrame">
            <qrcode-vue
              :value="acampante.id"
              :size="240"
              level="H"
              render-as="canvas"
            />
            <p class="qr-name">{{ acampante.name }}</p>
            <p class="qr-equipe" v-if="acampante.equipe">Equipe {{ acampante.equipe }}</p>
          </div>

          <div class="qr-actions">
            <button class="qr-btn" @click="downloadImage">
              <i class="fa fa-image"></i>
              <span>Salvar como imagem</span>
            </button>
            <button class="qr-btn" @click="printPdf">
              <i class="fa fa-file-pdf"></i>
              <span>Imprimir / Salvar PDF</span>
            </button>
          </div>
        </div>

        <p class="suporte-text">
          Caso precise alterar alguma informação, fale com a coordenação:
        </p>
        <a
          v-if="suporteLink"
          :href="suporteLink"
          target="_blank"
          rel="noopener"
          class="suporte-btn"
        >
          <i class="fa fa-whatsapp"></i>
          <span>Falar com a coordenação</span>
        </a>
      </div>

      <!-- Form -->
      <div v-else-if="state === 'editing'" class="form-wrap">
        <div class="aviso-uso-unico">
          <i class="fa fa-info-circle"></i>
          <div>
            <strong>Este link é de uso único e individual.</strong>
            <span>É exclusivo para sua inscrição. Não compartilhe com outras pessoas.</span>
          </div>
        </div>
        <InscricaoForm
          mode="public"
          :acampante="acampante"
          @submit-success="handleSubmitSuccess"
          @error="handleSubmitError"
        />
      </div>
    </main>

    <IgrejaAd />

    <footer class="inscricao-footer">
      <span>Acamp El Shaday · {{ year }}</span>
      <DevCredit />
    </footer>
  </div>
</template>

<script>
import InscricaoForm from '../components/InscricaoForm.vue'
import QrcodeVue from 'qrcode.vue'
import DevCredit from '../components/ui/DevCredit.vue'
import IgrejaAd from '../components/ui/IgrejaAd.vue'
import { getInscricao, buildSuporteWaLink } from '../lib/inscricao'

export default {
  name: 'InscricaoPage',
  components: { InscricaoForm, QrcodeVue, DevCredit, IgrejaAd },
  data() {
    return {
      state: 'loading',
      acampante: { name: '', equipe: null, id: '' },
      fichaPreenchidaEm: null,
      suporteContato: ''
    }
  },
  computed: {
    year() { return new Date().getFullYear() },
    suporteLink() {
      return buildSuporteWaLink(this.suporteContato, this.acampante?.name)
    }
  },
  async mounted() {
    await this.load()
  },
  methods: {
    async load() {
      const id = this.$route.params.id
      try {
        const data = await getInscricao(id)
        this.acampante = { id, name: data.name, equipe: data.equipe }
        this.fichaPreenchidaEm = data.fichaPreenchidaEm
        this.suporteContato = data.suporteContato
        this.state = data.fichaPreenchidaEm ? 'alreadySubmitted' : 'editing'
      } catch (err) {
        if (err?.response?.status === 404) {
          this.state = 'notFound'
        } else {
          this.state = 'notFound'
        }
      }
    },
    handleSubmitSuccess({ fichaPreenchidaEm }) {
      this.fichaPreenchidaEm = fichaPreenchidaEm
      this.state = 'alreadySubmitted'
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    handleSubmitError(err) {
      if (err?.code === 'JA_PREENCHIDA') {
        this.suporteContato = err.suporteContato || this.suporteContato
        this.fichaPreenchidaEm = new Date().toISOString()
        this.state = 'alreadySubmitted'
      }
    },
    formatDate(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}/${month}/${year}`
    },
    sanitizeFileName(s) {
      return (s || 'qrcode')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9_-]+/g, '_').toLowerCase()
    },
    getCanvas() {
      const frame = this.$refs.qrFrame
      if (frame && typeof frame.querySelector === 'function') {
        const canvas = frame.querySelector('canvas')
        if (canvas) return canvas
      }
      // Fallback: varre o DOM da própria seção pelo canvas
      const section = document.getElementById('qr-section')
      return section ? section.querySelector('canvas') : null
    },
    downloadImage() {
      const canvas = this.getCanvas()
      if (!canvas) return
      // Cria canvas maior com nome embaixo
      const padding = 20
      const labelH = 60
      const out = document.createElement('canvas')
      out.width = canvas.width + padding * 2
      out.height = canvas.height + padding * 2 + labelH
      const ctx = out.getContext('2d')
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, out.width, out.height)
      ctx.drawImage(canvas, padding, padding)
      ctx.fillStyle = '#222'
      ctx.textAlign = 'center'
      ctx.font = 'bold 16px Arial, sans-serif'
      ctx.fillText(this.acampante.name || '', out.width / 2, canvas.height + padding + 24)
      if (this.acampante.equipe) {
        ctx.font = '13px Arial, sans-serif'
        ctx.fillStyle = '#666'
        ctx.fillText(`Equipe ${this.acampante.equipe}`, out.width / 2, canvas.height + padding + 46)
      }
      const link = document.createElement('a')
      link.download = `checkin-acamp-${this.sanitizeFileName(this.acampante.name)}.png`
      link.href = out.toDataURL('image/png')
      link.click()
    },
    printPdf() {
      const canvas = this.getCanvas()
      if (!canvas) {
        window.print()
        return
      }
      const dataUrl = canvas.toDataURL('image/png')
      const w = window.open('', '_blank', 'width=600,height=800')
      if (!w) {
        window.print()
        return
      }
      const nameSafe = (this.acampante.name || '').replace(/</g, '&lt;')
      const equipeSafe = (this.acampante.equipe || '').replace(/</g, '&lt;')
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>QR Check-in — ${nameSafe}</title>
<style>
@page { margin: 24mm; }
* { box-sizing: border-box; }
body { font-family: -apple-system, Helvetica, Arial, sans-serif; margin: 0; padding: 32px; text-align: center; color: #222; }
h1 { font-size: 20px; margin: 0 0 4px 0; }
h2 { font-size: 24px; margin: 16px 0 4px; font-weight: 700; }
.equipe { font-size: 14px; color: #666; margin: 0 0 32px; text-transform: uppercase; letter-spacing: 1px; }
img { width: 320px; height: 320px; display: block; margin: 0 auto 24px; }
.footer { font-size: 12px; color: #888; margin-top: 40px; line-height: 1.5; }
.footer strong { color: #222; }
</style></head><body>
<h1>Acamp El Shaday</h1>
<p class="equipe">QR Code de check-in</p>
<img src="${dataUrl}" alt="QR Code" />
<h2>${nameSafe}</h2>
<p class="equipe">${equipeSafe ? 'Equipe ' + equipeSafe : ''}</p>
<p class="footer"><strong>Apresente este QR Code no portão de entrada</strong> para confirmar o seu check-in no acamp.</p>
</body></html>`
      w.document.open()
      w.document.write(html)
      w.document.close()
      // Aguarda imagem carregar antes de imprimir
      setTimeout(() => {
        try { w.focus(); w.print() } catch (e) { /* user pode imprimir manualmente */ }
      }, 500)
    }
  }
}
</script>

<style scoped>
.inscricao-public {
  min-height: 100vh;
  background: var(--color-bg-body);
  display: flex;
  flex-direction: column;
}

.inscricao-header {
  padding: 28px 16px 12px;
  text-align: center;
  background: var(--color-text-primary);
  color: #fff;
}
.logo {
  font-size: 26px;
  font-weight: 800;
  margin: 0;
  letter-spacing: 0.5px;
}
.logo span {
  font-weight: 300;
  opacity: 0.85;
}
.subtitle {
  margin: 6px 0 0;
  font-size: 13px;
  opacity: 0.75;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.inscricao-content {
  flex: 1;
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
  padding: 24px 16px;
  box-sizing: border-box;
}

.state-card {
  background: var(--color-bg-surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
  padding: 36px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}
.state-card h2 { margin: 4px 0; color: var(--color-text-primary); font-size: 20px; }
.state-card p { margin: 0; color: var(--color-text-secondary); font-size: 14px; line-height: 1.5; }

.spinner {
  width: 42px;
  height: 42px;
  border: 3px solid #e0e0e0;
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
  margin: 12px 0;
}
@keyframes spin { to { transform: rotate(360deg); } }

.state-success .check-icon {
  color: var(--color-success);
  font-size: 64px;
  line-height: 1;
}
.ficha-date {
  font-size: 12px;
  color: var(--color-text-muted);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.suporte-text {
  margin-top: 16px;
}
.suporte-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #25d366;
  color: #fff;
  padding: 12px 22px;
  border-radius: var(--radius-md);
  font-weight: 700;
  font-size: 14px;
  text-decoration: none;
  margin-top: 8px;
  transition: var(--transition-fast);
}
.suporte-btn:hover { background: #1ebe5d; }

.state-error i {
  color: var(--color-warning);
  font-size: 56px;
}

.form-wrap {
  background: var(--color-bg-surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
  padding: 24px;
}

.inscricao-footer {
  text-align: center;
  padding: 16px;
  font-size: 12px;
  color: var(--color-text-muted);
}

.aviso-uso-unico {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #e3f2fd;
  color: #0d47a1;
  border-left: 4px solid #1976d2;
  padding: 12px 16px;
  border-radius: var(--radius-md);
  margin-bottom: 20px;
  font-size: 13px;
  line-height: 1.4;
}
.aviso-uso-unico i { font-size: 18px; margin-top: 2px; flex-shrink: 0; }
.aviso-uso-unico strong { display: block; margin-bottom: 2px; }
.aviso-uso-unico span { font-size: 12px; opacity: 0.85; }

/* QR section */
.qr-section {
  width: 100%;
  margin: 24px 0 16px;
  padding: 20px 16px;
  background: #f8f9fa;
  border-radius: var(--radius-lg);
  border: 1px solid #e0e0e0;
}
.qr-title {
  font-size: 16px;
  font-weight: 800;
  margin: 0 0 8px;
  color: var(--color-text-primary);
}
.qr-instructions {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0 0 16px;
  line-height: 1.5;
}
.qr-instructions strong { color: var(--color-danger); }

.qr-frame {
  background: #fff;
  border-radius: var(--radius-md);
  padding: 20px;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.qr-name {
  margin: 8px 0 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text-primary);
  text-align: center;
}
.qr-equipe {
  margin: 0;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--color-text-muted);
  font-weight: 600;
}

.qr-actions {
  display: flex;
  gap: 10px;
  margin-top: 16px;
  justify-content: center;
  flex-wrap: wrap;
}
.qr-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: var(--radius-md);
  background: var(--color-accent);
  color: #fff;
  border: none;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: var(--transition-fast);
}
.qr-btn:hover { background: #2a54a3; }
.qr-btn:nth-child(2) { background: var(--color-text-primary); }
.qr-btn:nth-child(2):hover { background: #1a2530; }

@media (max-width: 540px) {
  .inscricao-content { padding: 16px 12px; }
  .form-wrap { padding: 16px; }
  .logo { font-size: 22px; }
}
</style>
